import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, TextField, CircularProgress,
  IconButton, Alert
} from '@mui/material';
import { BanknotesIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../config/currency';
import api from '../api/api';
import toast from 'react-hot-toast';
import AppIconButton from './ui/AppIconButton';
import { CURRENCY } from '../config/currency';
import { getPaymentMethods } from '../config/paymentMethods';

// Country-aware (UPI/Wallet are IN-only rails) — resolved at render time
const PAYMENT_METHODS = () => getPaymentMethods();

interface PaymentRow {
  id: number;
  method: string;
  amount: string;
}

interface SplitBillDialogProps {
  open: boolean;
  onClose: () => void;
  tableName: string;
  totalAmount: number;
  orderIds: number[];
  onSettleComplete: (result: any) => void;
}

const SplitBillDialog: React.FC<SplitBillDialogProps> = ({
  open, onClose, tableName, totalAmount, orderIds, onSettleComplete
}) => {
  const [payments, setPayments] = useState<PaymentRow[]>([
    { id: 1, method: '', amount: '' }
  ]);
  const [isSettling, setIsSettling] = useState(false);
  const [nextId, setNextId] = useState(2);

  useEffect(() => {
    if (!open) {
      setPayments([{ id: 1, method: '', amount: '' }]);
      setNextId(2);
    }
  }, [open]);

  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const remaining = Math.max(0, totalAmount - totalPaid);
  const overpaid = totalPaid > totalAmount;
  const allValid = payments.every(p => p.method && parseFloat(p.amount) > 0);

  const addRow = () => {
    setPayments(prev => [...prev, { id: nextId, method: '', amount: '' }]);
    setNextId(n => n + 1);
  };

  const removeRow = (id: number) => {
    if (payments.length <= 1) return;
    setPayments(prev => prev.filter(p => p.id !== id));
  };

  const updateRow = (id: number, field: 'method' | 'amount', value: string) => {
    setPayments(prev => prev.map(p =>
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const fillRemaining = (id: number) => {
    const otherTotal = payments
      .filter(p => p.id !== id)
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const fill = Math.max(0, totalAmount - otherTotal);
    updateRow(id, 'amount', fill.toFixed(2));
  };

  const handleSettle = async () => {
    if (!allValid || overpaid) return;
    setIsSettling(true);
    try {
      const response = await api.post('/orders/split-settle', {
        orderIds,
        payments: payments.map(p => ({
          paymentMethod: p.method,
          amount: parseFloat(p.amount)
        }))
      });
      const data = response.data;
      if (data.fullyPaid) {
        toast.success('Bill settled successfully with split payment');
      } else {
        toast.success(`Partial payment recorded. Remaining: ${formatCurrency(data.remainingAmount)}`);
      }
      onSettleComplete(data);
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to process split payment');
    } finally {
      setIsSettling(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1400 }}
      PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #ede9fe 0%, #ffffff 100%)',
        borderBottom: '1px solid #a78bfa', /* basil-todo */
        fontWeight: 700, fontSize: '1.25rem', color: 'var(--accent-alt)',
        display: 'flex', alignItems: 'center', gap: 1
      }}>
        <BanknotesIcon style={{ width: 24, height: 24 }} />
        Split Bill — {tableName}
      </DialogTitle>
      <DialogContent sx={{ padding: '20px', background: 'white' }}>
        {/* Total Amount */}
        <Box sx={{
          background: 'var(--bg-sunken)', p: 2, borderRadius: '8px', mb: 3,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total Bill:</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-secondary)' }}>
            {formatCurrency(totalAmount)}
          </Typography>
        </Box>

        {/* Payment Rows */}
        {payments.map((row, idx) => (
          <Box key={row.id} sx={{
            mb: 2, p: 2, borderRadius: '8px',
            border: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-sunken)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--text-tertiary)' }}>
                Payment {idx + 1}
              </Typography>
              {payments.length > 1 && (
                <AppIconButton
                  icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                  tooltip="Remove payment"
                  variant="danger"
                  size="small"
                  onClick={() => removeRow(row.id)}
                  data-testid={`remove-payment-${row.id}`}
                />
              )}
            </Box>

            {/* Payment Method Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
              {PAYMENT_METHODS().map(m => (
                <Chip
                  key={m.value}
                  label={`${m.icon} ${m.label}`}
                  onClick={() => updateRow(row.id, 'method', m.value)}
                  variant={row.method === m.value ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    fontWeight: row.method === m.value ? 600 : 400,
                    backgroundColor: row.method === m.value ? '#8b5cf6' : 'white',
                    color: row.method === m.value ? 'white' : '#374151',
                    borderColor: row.method === m.value ? '#8b5cf6' : '#d1d5db',
                    '&:hover': {
                      backgroundColor: row.method === m.value ? '#7c3aed' : '#f3f4f6'
                    }
                  }}
                />
              ))}
            </Box>

            {/* Amount */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                value={row.amount}
                onChange={e => {
                  if (/^\d*\.?\d*$/.test(e.target.value)) updateRow(row.id, 'amount', e.target.value);
                }}
                type="text"
                inputMode="decimal"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>{CURRENCY.symbol}</Typography>
                }}
              />
              <Button
                size="small"
                variant="text"
                onClick={() => fillRemaining(row.id)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: 'var(--accent-alt)', fontSize: '0.75rem' }}
              >
                Fill rest
              </Button>
            </Box>
          </Box>
        ))}

        {/* Add Payment Row */}
        <Button
          startIcon={<PlusCircleIcon style={{ width: 18, height: 18 }} />}
          onClick={addRow}
          sx={{ textTransform: 'none', color: 'var(--accent-alt)', mb: 2 }}
        >
          Add Payment
        </Button>

        {/* Summary */}
        <Box sx={{
          background: overpaid ? '#fef2f2' : (remaining === 0 ? '#f0fdf4' : '#ede9fe'),
          p: 2, borderRadius: '8px',
          border: `1px solid ${overpaid ? '#fca5a5' : (remaining === 0 ? '#86efac' : '#a78bfa')}`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Paid:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: overpaid ? '#dc2626' : '#059669' }}>
              {formatCurrency(totalPaid)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Remaining:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: remaining > 0 ? '#7c3aed' : '#059669' }}>
              {formatCurrency(remaining)}
            </Typography>
          </Box>
        </Box>

        {overpaid && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            Total payments exceed the bill amount.
          </Alert>
        )}
        {remaining > 0 && totalPaid > 0 && !overpaid && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            This will record a partial payment. The table will remain occupied.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{
        padding: '12px 20px', background: 'var(--accent-alt-bg)',
        borderTop: '1px solid #a78bfa', gap: '8px' /* basil-todo */
      }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSettling}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSettle}
          disabled={isSettling || !allValid || overpaid || totalPaid === 0}
          sx={{
            backgroundColor: 'var(--accent-alt)',
            '&:hover': { backgroundColor: 'var(--accent-alt)' },
            '&.Mui-disabled': { backgroundColor: '#c4b5fd', color: 'var(--accent-alt)' } /* basil-todo */
          }}
        >
          {isSettling ? (
            <><CircularProgress size={16} sx={{ mr: 1, color: 'white' }} /> Processing...</>
          ) : (
            `Settle ${formatCurrency(totalPaid)}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitBillDialog;
